import * as React from "react";

import {includes} from "lodash-es";

import {Icon} from "@client/shared/typography";

import {Label, QuickSelect, SelectText, TextInput} from "@client/forms";
import {server} from "@reactivated";

// Always put Breakfast, Lunch, Dinner, Snack first, then sort alphabetically.
const sortMeals = <T extends {label: string}>(a: T, b: T) => {
    const order = ["Breakfast", "Lunch", "Dinner", "Snack"];
    const indexA = order.indexOf(a.label);
    const indexB = order.indexOf(b.label);

    if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
    } else if (indexA !== -1) {
        return -1;
    } else if (indexB !== -1) {
        return 1;
    } else {
        return a.label.localeCompare(b.label);
    }
};

// We don't want these shifting around, especially during a quick cycle of
// add another etc (recent meals come back sorted by recency).
// TODO: This needs better work. Maybe try to figure out how to pin meal order?
// A hardcoded sort could always make Breakfast, Lunch, Dinner, Snack, then anything else happen.
export const getMealOptionsForDay = (day: server.journal.nutrition.Day.output) => {
    const dayMeals = day.meals
        .filter((m) => m.name != "__SUPPLEMENTS__")
        .filter((m) => m.type != "AUTO_CREATED")
        .map((m) => ({
            value: m.name,
            label: m.name,
        }));
    const dayNames = dayMeals.map((m) => m.label);
    const recentMeals = day.recent_meals
        .filter((name) => name != "__SUPPLEMENTS__")
        .map((name) => ({
            value: name,
            label: name,
        }))
        .filter((m) => !includes(dayNames, m.label));

    return {
        allMeals: dayMeals
            .concat(recentMeals)
            .slice()
            .sort(sortMeals)
            .map((m) => m.value)
            .reduce<string[]>((acc, m) => {
                if (!includes(acc, m)) {
                    acc.push(m);
                }
                return acc;
            }, [])
            .map((m) => ({value: m, label: m})),
    };
};

interface Props {
    day: server.journal.nutrition.Day.output;
    onAuto: () => void;
    onMealChange: (meal: string | null) => void;
    value: string | null;
    error: string | null;
}

export function MealSelector(props: Props) {
    const {allMeals} = getMealOptionsForDay(props.day);
    const [mode, setMode] = React.useState<"existing" | "new">(
        props.value == null || props.value == "" ? "new" : "existing",
    );

    const onSelect = (value: string | null) => {
        if (value == "" || value == null) {
            setMode("new");
            props.onMealChange(null);
        } else {
            setMode("existing");
            props.onMealChange(value);
        }
    };

    return (
        <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            <label>
                <div className="mb-2 flex items-center justify-between">
                    <p className="text-base">Choose a meal or create a new one:</p>
                    <button
                        onClick={props.onAuto}
                        className="
                            ml-2 rounded-sm bg-white p-1 text-xs font-medium
                            text-gray-900 shadow-xs ring-1 ring-stone-300 ring-inset
                            hover:bg-stone-50
                        "
                    >
                        <Icon icon="custom-bolder-cross" className="size-3!" />
                    </button>
                </div>
                {allMeals.length > 6 ? (
                    <SelectText name="" onChange={onSelect} value={props.value ?? ""}>
                        <option value="">{"<Create new meal>"}</option>
                        {allMeals.map(({label, value}) => (
                            <option key={value.toString()} value={value}>
                                {label}
                            </option>
                        ))}
                    </SelectText>
                ) : (
                    <QuickSelect
                        type="text"
                        name=""
                        options={[...allMeals, {value: null, label: "+"}]}
                        value={mode == "existing" ? props.value : null}
                        onChange={onSelect}
                        shrinkLast={true}
                    />
                )}
            </label>
            {mode == "new" && (
                <Label label={null} error={props.error}>
                    <div className="mb-1 text-base">Meal name</div>
                    <TextInput
                        name="meal_name"
                        ariaLabel="meal_name"
                        readOnly={false}
                        onChange={props.onMealChange}
                        value={props.value}
                    />
                </Label>
            )}
        </div>
    );
}
